import { action, makeObservable, observable, computed, runInAction } from "mobx";
import { BasePaymentViewStore } from "application/payment/stores";
import { currencyFormatter } from "mrb/common/formatting";
import { MrbModalParams } from "mrb/common/models";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";
import { localizationService } from "common/localization";
import { applicationErrorCodes } from "common/constants";
import ReactGA from "react-ga4";

class TakePaymentViewStore extends BasePaymentViewStore {
    planId = null;
    userAccessType = null;
    @observable couponCode = "";
    @observable.ref appliedCoupon = null;
    @observable couponErrorCode = null;
    @observable isApplyingCoupon = false;
    @observable.ref certifiedModes = [];
    @observable selectedCertifiedModeId = null;
    @observable isWhitelistMode = false;

    constructor(rootStore, { routeStore }) {
        super(rootStore, { routeStore });
        makeObservable(this);
        this.planId = rootStore.routerStore.routerState.params.planId;
        this.userAccessType = rootStore.premiumSubscriptionStore.nakiRadioUserAccessType;
        this.onClickSubscribe = this.onClickSubscribe.bind(this);
        this.certifiedModeInfoModal = new MrbModalParams(null, null);
        this.selectedCertifiedModeId = this.rootStore.premiumSubscriptionStore.certifiedModeId;
        this.isWhitelistMode = this.rootStore.premiumSubscriptionStore.isWhitelistMode;
        this.openCertifiedModeInfoModal = this.openCertifiedModeInfoModal.bind(this);
        this.modeSelectStore = new MrbSelectStore({
            isSearchable: false,
            virtualized: false,
            initFetch: false,
            isClearable: false,
            textKey: "name",
            valueKey: "id",
            actions: {
                onChange: (option) => {
                    if (option) {
                        this.setCertifiedModeSelection(option.value);
                    }
                },
            },
        });
        this.modeSelectStore.originalItems = new DropdownItemsResult([], 1, 0, 0);
        this.loadCertifiedModes();
    }

    async onInit() {
        return this.fetchSubscriptionPlan();
    }

    async fetchSubscriptionPlan() {
        try {
            const subscriptionPlan = await this.routeStore.getSubscriptionPlan(this.planId);
            this.setSubscriptionPlan(subscriptionPlan);
        } catch (err) {
            this.rootStore.notificationStore.error("Unexpected error occurred. Please contact the support team.", err);
        }
    }

    onClickSubscribe(cardElements) {
        if (this.paymentMethodExist) {
            this.subscribeUsingExistingPaymentMethod();
        } else {
            this.submitPayment(cardElements);
        }
    }

    openCertifiedModeInfoModal() {
        this.certifiedModeInfoModal.open();
    }

    @action.bound
    async setCertifiedModeSelection(value) {
        const prevCertifiedModeId = this.selectedCertifiedModeId;
        const prevWhitelistMode = this.isWhitelistMode;

        let certifiedModeId = null;
        let whitelistMode = false;
        if (value === "whitelist") {
            whitelistMode = true;
        } else if (value !== "naki-standard") {
            certifiedModeId = value;
        }

        this.selectedCertifiedModeId = certifiedModeId;
        this.isWhitelistMode = whitelistMode;

        try {
            await this.routeStore.updateCertifiedMode(certifiedModeId, whitelistMode);
            const selectedMode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            this.rootStore.premiumSubscriptionStore.setCertifiedMode(
                certifiedModeId,
                selectedMode ? selectedMode.name : null,
                whitelistMode
            );
            this.rootStore.notificationStore.success(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.SUCCESS_MESSAGE")
            );
        } catch (error) {
            this.selectedCertifiedModeId = prevCertifiedModeId;
            this.isWhitelistMode = prevWhitelistMode;
            this._syncModeSelectStore(prevCertifiedModeId, prevWhitelistMode);
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.ERROR_MESSAGE"),
                error
            );
        }
    }

    @action.bound
    async loadCertifiedModes() {
        try {
            const response = await this.routeStore.getCertifiedModes();
            this.certifiedModes = response.item || response || [];
        } catch (error) {
            this.certifiedModes = [];
        }
        const allOptions = [
            { id: "naki-standard", name: "The Naki Standard" },
            { id: "whitelist", name: "Whitelist Mode" },
            ...this.certifiedModes.map((m) => ({
                id: m.id,
                name: `${m.name}`,
            })),
        ];
        this.modeSelectStore.originalItems = new DropdownItemsResult(allOptions, 1, allOptions.length, allOptions.length);
        this._syncModeSelectStore(this.selectedCertifiedModeId, this.isWhitelistMode);
    }

    _syncModeSelectStore(certifiedModeId, isWhitelistMode) {
        let option;
        if (certifiedModeId) {
            const mode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            option = mode ? { id: mode.id, name: `${mode.name}` } : { id: "naki-standard", name: "The Naki Standard" };
        } else if (isWhitelistMode) {
            option = { id: "whitelist", name: "Whitelist Mode" };
        } else {
            option = { id: "naki-standard", name: "The Naki Standard" };
        }
        this.modeSelectStore.setSelectedItem(option);
    }

    @action.bound
    onChangeCouponCode(value) {
        this.couponCode = value;
        this.couponErrorCode = null;
    }

    @action.bound
    async applyCoupon() {
        const code = (this.couponCode || "").trim();
        if (!code) {
            return;
        }
        this.isApplyingCoupon = true;
        this.couponErrorCode = null;
        try {
            const result = await this.routeStore.validatePromoCode(code, this.planId);
            runInAction(() => {
                this.appliedCoupon = result;
                this.couponCode = result.promoCode;
            });
        } catch (err) {
            runInAction(() => {
                this.appliedCoupon = null;
                this.couponErrorCode = err && err.data ? err.data.errorCode : null;
            });
        } finally {
            runInAction(() => {
                this.isApplyingCoupon = false;
            });
        }
    }

    @action.bound
    removeCoupon() {
        this.appliedCoupon = null;
        this.couponErrorCode = null;
        this.couponCode = "";
    }

    @computed
    get couponErrorMessageKey() {
        switch (this.couponErrorCode) {
            case applicationErrorCodes.payment.promoCodeExpired:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.EXPIRED";
            case applicationErrorCodes.payment.promoCodeRedemptionLimitReached:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.LIMIT_REACHED";
            case applicationErrorCodes.payment.promoCodeRestrictionNotMet:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.RESTRICTION";
            case applicationErrorCodes.payment.promoCodeNotFound:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NOT_VALID";
            case applicationErrorCodes.payment.promoCodeAlreadyRedeemed:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.ALREADY_USED";
            case applicationErrorCodes.payment.promoCodeNewCustomersOnly:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NEW_CUSTOMERS_ONLY";
            case null:
            case undefined:
                return null;
            default:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.GENERIC";
        }
    }

    formatPrice(value) {
        return currencyFormatter.format(value, { currency: "USD" });
    }

    buildChargeDialogCopy() {
        const fullPrice = this.formatPrice(this.subscriptionPlan.price);
        if (this.discountSummary) {
            const firstChargeFormatted = this.formatPrice(this.discountSummary.firstCharge);
            return {
                description: `Your card will be charged ${firstChargeFormatted} now, cancel anytime.`,
                yesLabel: `Pay ${firstChargeFormatted}`,
            };
        }
        return {
            description: `Your card will be charged ${fullPrice} each month, cancel anytime.`,
            yesLabel: `Pay ${fullPrice}`,
        };
    }

    @computed
    get discountSummary() {
        if (!this.appliedCoupon || !this.subscriptionPlan) {
            return null;
        }
        const price = this.subscriptionPlan.price;
        const { percentOff, amountOff, duration, durationInMonths } = this.appliedCoupon;
        const months = durationInMonths || 1;

        if (percentOff === 100) {
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_FOREVER", params: {}, firstCharge: 0 };
            }
            const m = duration === "repeating" ? months : 1;
            const key =
                m === 1
                    ? "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_ONE_MONTH"
                    : "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_MONTHS";
            return { key, params: { months: m, price: this.formatPrice(price) }, firstCharge: 0 };
        }

        if (typeof percentOff === "number" && percentOff > 0) {
            const discounted = Math.max(Math.round(price * (1 - percentOff / 100) * 100) / 100, 0);
            const base = {
                percent: percentOff,
                price: this.formatPrice(price),
                discountedPrice: this.formatPrice(discounted),
            };
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_FOREVER", params: base, firstCharge: discounted };
            }
            if (duration === "repeating") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_REPEATING", params: { ...base, months }, firstCharge: discounted };
            }
            return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_ONCE", params: base, firstCharge: discounted };
        }

        if (typeof amountOff === "number" && amountOff > 0) {
            const discounted = Math.max(Math.round((price - amountOff) * 100) / 100, 0);
            const base = {
                amount: this.formatPrice(amountOff),
                price: this.formatPrice(price),
                discountedPrice: this.formatPrice(discounted),
            };
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_FOREVER", params: base, firstCharge: discounted };
            }
            if (duration === "repeating") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_REPEATING", params: { ...base, months }, firstCharge: discounted };
            }
            return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_ONCE", params: base, firstCharge: discounted };
        }

        return null;
    }

    @action.bound
    subscribeUsingExistingPaymentMethod() {
        const promoCode = this.appliedCoupon ? this.appliedCoupon.promoCode : null;
        const { description, yesLabel } = this.buildChargeDialogCopy();
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Ready to start your subscription?",
            description,
            yesLabel,
            onConfirm: async () => {
                try {
                    await this.routeStore.subscribeUsingExistingPaymentMethod(this.planId, promoCode);
                    this.paymentSuccessModal.open({
                        subscriptionPlan: this.subscriptionPlan.name,
                        modeSelectStore: this.modeSelectStore,
                        onClickContentModeInfo: this.openCertifiedModeInfoModal,
                    });
                    ReactGA.event("subscribe", {
                        plan: this.subscriptionPlan.name,
                        internal_user_id: this.rootStore.userStore.user.id,
                    });
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "Unexpected error occurred while trying to process payment. Please contact the support team.",
                        err
                    );
                }
            },
        });
    }

    @action.bound
    submitPayment(cardElements) {
        const promoCode = this.appliedCoupon ? this.appliedCoupon.promoCode : null;
        const { description, yesLabel } = this.buildChargeDialogCopy();
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Ready to start your subscription?",
            description,
            yesLabel,
            onConfirm: async () => {
                try {
                    const { user } = this.rootStore.userStore;
                    this.stripeField.resetValidation();
                    const result = await this.routeStore.preprocessPayment(this.planId, promoCode);
                    let payload = null;
                    let paymentMethod = null;
                    if (result.clientSecret) {
                        payload = await this.stripe.confirmCardPayment(result.clientSecret, {
                            payment_method: {
                                card: cardElements,
                            },
                            receipt_email: user.email,
                        });
                    } else {
                        const setupPaymentMethodResponse = await this.routeStore.setupPaymentMethodRequest();
                        payload = await this.stripe.confirmCardSetup(setupPaymentMethodResponse.token, {
                            payment_method: {
                                card: cardElements,
                            },
                        });
                        if (!payload.error) {
                            paymentMethod = payload.setupIntent.payment_method;
                        }
                    }

                    if (payload.error) {
                        this.stripeField.setError(payload.error);
                        const data = {
                            undoChanges: true,
                            paymentProviderSubscriptionId: result.paymentProviderSubscriptionId,
                        };
                        await this.routeStore.postprocessPayment(data);
                    } else {
                        const data = {
                            paymentProviderPaymentMethodId: paymentMethod,
                            paymentProviderSubscriptionId: result.paymentProviderSubscriptionId,
                        };
                        await this.routeStore.postprocessPayment(data);
                        this.paymentSuccessModal.open({
                            subscriptionPlan: this.subscriptionPlan.name,
                            modeSelectStore: this.modeSelectStore,
                            onClickContentModeInfo: this.openCertifiedModeInfoModal,
                        });
                        ReactGA.event("subscribe", {
                            plan: this.subscriptionPlan.name,
                            internal_user_id: this.rootStore.userStore.user.id,
                        });
                    }
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "Unexpected error occurred while trying to process payment. Please contact the support team.",
                        err
                    );
                }
            },
        });
    }
}

export default TakePaymentViewStore;
